import { useQuery } from "@tanstack/react-query"
import { motion } from "framer-motion"
import { Archive, Bot, Plus, Wrench } from "lucide-react"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { botBuilderKeys, listArchivedBots, listBots, listHelperBots } from "@/entities/bot-builder"
import { botReachKey, getBotReach } from "@/entities/bot-stats"
import { useSessionRole } from "@/entities/session"
import { AddBotDialog } from "@/features/create-bot"
import { staggerContainer } from "@/shared/lib/motion"
import { useIntro } from "@/shared/lib/use-shown-key"
import { cn } from "@/shared/lib/utils"
import { AccessDenied } from "@/shared/ui/access-denied"
import { Button } from "@/shared/ui/button"
import { EmptyState } from "@/shared/ui/empty-state"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { PageShell } from "@/shared/ui/page-shell"
import { BotRow, BotRowSkeleton, ReachPanel, ReachPanelSkeleton } from "@/widgets/bots-list"
import { LoadSwap } from "@/shared/ui/load-swap"
import { Reveal } from "@/shared/ui/reveal"
import { SlidingPill } from "@/shared/ui/sliding-pill"
import { TabCount } from "@/shared/ui/tab-label"

/**
 * Боты клуба. Сценарий у каждого бота свой и собирается внутри бота, поэтому отдельного
 * списка сценариев здесь нет. Архив ведёт админ, он открывается переключателем.
 */
export function BotsPage() {
	const navigate = useNavigate()
	const { isAdmin, isModerator } = useSessionRole()
	const [params, setParams] = useSearchParams()
	const tab = isAdmin && params.get("tab") === "archive"
		? "archive"
		: isAdmin && params.get("tab") === "helpers"
			? "helpers"
			: "bots"
	const archive = tab === "archive"
	const [adding, setAdding] = useState(false)

	const bots = useQuery({ queryKey: botBuilderKeys.bots, queryFn: listBots, enabled: isModerator })
	const archived = useQuery({
		queryKey: botBuilderKeys.archivedBots,
		queryFn: listArchivedBots,
		enabled: isAdmin,
	})

	const helpers = useQuery({
		queryKey: botBuilderKeys.helperBots,
		queryFn: listHelperBots,
		enabled: isAdmin,
	})

	const reach = useQuery({ queryKey: botReachKey, queryFn: getBotReach, enabled: isModerator })
	const reachByBot = new Map((reach.data?.bots ?? []).map((row) => [row.botId, row]))

	const list = tab === "archive" ? archived : tab === "helpers" ? helpers : bots
	const intro = useIntro(tab, !!list.data)

	if (!isModerator) return <AccessDenied title="Боты" />

	return (
		<PageShell
			eyebrow="Автоматизация"
			title="Боты"
			subtitle="У каждого бота свой сценарий: воронки, меню, рассылки и аналитика внутри бота."
			action={
				isAdmin ? (
					<Button className="rounded-full px-5" onClick={() => setAdding(true)}>
						<Plus />
						Добавить бота
					</Button>
				) : null
			}
		>
			{isAdmin ? (
				<div
					className="relative mb-5 flex gap-1 rounded-full bg-secondary/60 p-1 sm:w-fit"
					role="tablist"
				>
					<SlidingPill className="rounded-full" />
					{[
						{ value: "bots", label: "Работают", count: bots.data?.length, icon: Bot },
						{ value: "helpers", label: "Вспомогательные", count: helpers.data?.length, icon: Wrench },
						{ value: "archive", label: "Архив", count: archived.data?.length, icon: Archive },
					].map((item) => (
						<button
							key={item.label}
							type="button"
							role="tab"
							aria-selected={tab === item.value}
							data-active={tab === item.value}
							onClick={() =>
								setParams(item.value === "bots" ? {} : { tab: item.value }, { replace: true })
							}
							className={cn(
								"relative flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-1.5 text-sm transition-colors sm:flex-none",
								tab === item.value
									? "text-foreground"
									: "text-muted-foreground hover:text-foreground",
							)}
						>
							<item.icon className="relative size-4" />
							<span className="relative flex items-center">
								{item.label}
								<TabCount value={item.count} />
							</span>
						</button>
					))}
				</div>
			) : null}

			{/* Охват относится только к работающим ботам: в архиве панель сворачивается, и список
			    поднимается плавно, а не прыжком. */}
			<Reveal show={tab === "bots" && !reach.isError} className="pb-6">
				<LoadSwap loading={reach.isLoading}>
					{reach.data ? <ReachPanel reach={reach.data} /> : <ReachPanelSkeleton />}
				</LoadSwap>
			</Reveal>

			<LoadSwap
				loading={list.isLoading}
				id={list.isError ? "error" : list.data?.length ? `list-${tab}` : `empty-${tab}`}
			>
				{list.isLoading ? (
					<ul className="space-y-2">
						<BotRowSkeleton />
						<BotRowSkeleton />
					</ul>
				) : list.isError ? (
					<LoadErrorState onRetry={() => void list.refetch()} />
				) : list.data?.length ? (
					<motion.ul
						variants={intro ? staggerContainer(0.04) : undefined}
						initial={intro ? "hidden" : false}
						animate={intro ? "visible" : undefined}
						className="space-y-2"
					>
						{list.data.map((bot) => (
							<BotRow key={bot.id} bot={bot} reach={reachByBot.get(bot.id)} animate={intro} />
						))}
					</motion.ul>
				) : tab === "helpers" ? (
					<EmptyState icon={Wrench} title="Вспомогательных ботов нет">
						Сюда попадают боты, которые только ищут Telegram ID. Пометить бота можно в его
						настройках.
					</EmptyState>
				) : archive ? (
					<EmptyState icon={Archive} title="Архив пуст">
						Сюда попадают боты, убранные из работы. Их платежи и аудитория сохраняются.
					</EmptyState>
				) : (
					<EmptyState
						icon={Bot}
						title="Ботов пока нет"
						action={isAdmin ? <Button onClick={() => setAdding(true)}>Добавить бота</Button> : null}
					>
						Создайте бота в @BotFather и добавьте его по токену.
					</EmptyState>
				)}
			</LoadSwap>

			<AddBotDialog
				open={adding}
				onOpenChange={setAdding}
				onCreated={(bot) => {
					setAdding(false)
					navigate(`/admin/bots/${bot.id}`)
				}}
			/>
		</PageShell>
	)
}
